import type { IdentifyMeta } from "../lib/types";
import { useAuth } from "../lib/auth";

// Admin-only diagnostic signal under the results so the operator can judge —
// over real use — whether the current free model is good enough or it's time
// to move to a stronger one (Llama 70B, Anthropic Opus, …). Regular users
// never see model labels / repair status / README pointers — those are
// developer diagnostics, not snorkeler-facing copy.

const QUALITY_STYLE: Record<
  IdentifyMeta["quality"],
  { label: string; className: string }
> = {
  ok: { label: "ok", className: "text-aqua" },
  weak: { label: "weak", className: "text-coral" },
  poor: { label: "poor", className: "text-coral" },
};

export function QualityNote({ meta }: { meta: IdentifyMeta }) {
  const auth = useAuth();
  const isAdmin = auth.status === "authed" && auth.isAdmin;
  // Diagnostics are operator-only. For everyone else, render nothing — the
  // user-facing "try a clearer photo / describe instead" nudges live in the
  // modals themselves.
  if (!isAdmin) return null;

  const q = QUALITY_STYLE[meta.quality];
  return (
    <div className="mt-3 border-t border-ink/5 pt-3 text-xs text-ink/45">
      <span>{meta.model_label}</span>
      <span aria-hidden> · </span>
      <span>top match {Math.round(meta.top_confidence * 100)}%</span>
      <span aria-hidden> · </span>
      <span>
        quality:{" "}
        <span className={`font-semibold ${q.className}`}>{q.label}</span>
      </span>
      {meta.json_repaired && (
        <span aria-hidden> · output needed cleanup</span>
      )}
      {meta.quality !== "ok" && (
        <p className="mt-1 text-ink/40">
          Low-confidence result — if this keeps happening, switch to a
          stronger model (see README → Quality check).
        </p>
      )}
    </div>
  );
}
